<template>
  <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-100 dark:border-gray-800">
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-lg font-semibold">Operational Status</h3>
      <GlassSelect
        v-model="currentView"
        @change="saveViewPreference"
        :options="[
          { value: 'doughnut', label: 'Doughnut Chart' },
          { value: 'bar', label: 'Bar Chart' },
          { value: 'numbers', label: 'Numbers View' },
        ]"
        :valueKey="'value'"
        :labelKey="'label'"
        placeholder="Select View"
        class="w-auto"
      />
    </div>

    <!-- Numbers View -->
    <div v-if="currentView === 'numbers'" class="space-y-4">
      <div
        v-for="(item, index) in chartData"
        :key="index"
        class="flex items-center justify-between"
      >
        <span class="text-gray-600 dark:text-white">{{ item.name }}</span>
        <span class="text-gray-900 dark:text-white font-medium">{{ item.value }}</span>
      </div>
      <div class="pt-2 border-t border-gray-200">
        <div class="flex items-center justify-between">
          <span class="text-gray-600 dark:text-white font-semibold">Total Probes</span>
          <span class="text-gray-900 dark:text-white font-semibold">{{ probeData.total }}</span>
        </div>
      </div>
    </div>

    <!-- Doughnut Chart View -->
    <div v-else-if="currentView === 'doughnut'" class="h-80">
      <Doughnut :data="doughnutData" :options="doughnutOptions" />
    </div>

    <!-- Bar Chart View -->
    <div v-else class="h-80">
      <Bar :data="barData" :options="chartOptions" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import Cookies from "js-cookie";
import GlassSelect from "@/components/GlassSelect.vue";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Bar, Doughnut } from "vue-chartjs";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartDataLabels
);

const props = defineProps({
  probeData: {
    type: Object,
    required: true,
    default: () => ({
      total: 0,
      by_status: {
        is_installed: 0,
        in_maintenance: 0,
        pending: 0,
      },
    }),
  },
});

const currentView = ref("doughnut");
const COOKIE_NAME = "preferred-operational-view";
const COOKIE_EXPIRES = 30;

onMounted(() => {
  const savedView = Cookies.get(COOKIE_NAME);
  if (savedView && ["doughnut", "bar", "numbers"].includes(savedView)) {
    currentView.value = savedView;
  }
});

const saveViewPreference = () => {
  Cookies.set(COOKIE_NAME, currentView.value, { expires: COOKIE_EXPIRES });
};

// Colors for different statuses
const colors = {
  installed: "#22C55E", // green-500
  maintenance: "#3B82F6", // blue-500
  pending: "#EAB308", // yellow-500
};

// Transform data for charts
const chartData = computed(() => [
  {
    name: "Installed",
    value: props.probeData.by_status.is_installed || 0,
    color: colors.installed,
  },
  {
    name: "In Maintenance",
    value: props.probeData.by_status.in_maintenance || 0,
    color: colors.maintenance,
  },
  {
    name: "Pending Changes",
    value: props.probeData.by_status.pending || 0,
    color: colors.pending,
  },
]);

// Calculate max value for y-axis scaling
const maxValue = computed(() =>
  Math.max(...chartData.value.map((item) => item.value))
);
const suggestedMax = computed(() => Math.ceil(maxValue.value * 1.2));

// Doughnut Chart Data and Options
const doughnutData = computed(() => ({
  labels: chartData.value.map((item) => item.name),
  datasets: [
    {
      data: chartData.value.map((item) => item.value),
      backgroundColor: chartData.value.map((item) => item.color),
      borderColor: "white",
      borderWidth: 2,
    },
  ],
}));

const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "65%",
  plugins: {
    legend: {
      position: "right",
      labels: {
        usePointStyle: true,
        padding: 20,
        font: { size: 12 },
      },
    },
    tooltip: {
      callbacks: {
        label: function (context) {
          const value = context.raw;
          const total = props.probeData.total;
          const percentage = ((value / total) * 100).toFixed(1);
          return `${value} probes (${percentage}%)`;
        },
      },
    },
    datalabels: {
      color: "rgb(17 24 39)",
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      borderRadius: 4,
      padding: { left: 4, right: 4, top: 2, bottom: 2 },
      font: {
        weight: "bold",
        size: 13,
      },
      formatter: (value) => {
        if (value === 0) return "";
        const percentage = ((value / props.probeData.total) * 100).toFixed(1);
        return `${percentage}%`;
      },
      textAlign: "center",
    },
  },
};

// Bar Chart Data and Options
const barData = computed(() => ({
  labels: chartData.value.map((item) => item.name),
  datasets: [
    {
      label: "Probes",
      data: chartData.value.map((item) => item.value),
      backgroundColor: chartData.value.map((item) => item.color),
      borderColor: chartData.value.map((item) => item.color),
      borderWidth: 1,
      borderRadius: 4,
      barPercentage: 0.6,
    },
  ],
}));

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      callbacks: {
        label: function (context) {
          const value = context.raw;
          const total = props.probeData.total;
          const percentage = ((value / total) * 100).toFixed(1);
          return `${value} probes (${percentage}%)`;
        },
      },
    },
    datalabels: {
      anchor: "end",
      align: "top",
      offset: 4,
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      borderRadius: 4,
      padding: { left: 4, right: 4, top: 2, bottom: 2 },
      color: "rgb(17 24 39)",
      font: {
        weight: "bold",
        size: 12,
      },
      formatter: (value) => value.toLocaleString(),
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        precision: 0,
      },
      grid: {
        display: true,
      },
      suggestedMax: suggestedMax.value,
    },
    x: {
      grid: {
        display: false,
      },
    },
  },
}));
</script>
